import { type FormEvent, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { APP_NAME, ApiError, authApi } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";

export function ResetPasswordPage() {
  const { t } = useAppPreferences();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = useMemo(() => searchParams.get("token") ?? "", [searchParams]);
  const [token, setToken] = useState(tokenFromUrl);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSaved(false);

    if (password.length < 8) {
      setError(t("Password must be at least 8 characters."));
      return;
    }

    if (password !== confirmPassword) {
      setError(t("Passwords do not match."));
      return;
    }

    setSubmitting(true);
    try {
      await authApi.resetPassword(token.trim(), password);
      setSaved(true);
      setPassword("");
      setConfirmPassword("");
    } catch (requestError) {
      setError(
        requestError instanceof ApiError
          ? requestError.detail
          : t("Unable to reset password. Please try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="shell-grid min-h-screen px-6 py-10 text-slate-100">
      <div className="mx-auto grid w-full max-w-3xl gap-6">
        <form
          className="glass-panel rounded-[32px] border border-white/10 p-8 sm:p-10"
          onSubmit={handleSubmit}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-sky-200">
            {t("{appName} account recovery", { appName: APP_NAME })}
          </p>
          <div className="mt-5 space-y-2">
            <h1 className="text-4xl font-semibold tracking-tight text-white">
              {t("Choose a new password")}
            </h1>
            <p className="text-sm leading-7 text-slate-300">
              {t("Use the reset token from the email link and set a password with at least 8 characters.")}
            </p>
          </div>

          <div className="mt-8 grid gap-4">
            <label className="block">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
                {t("Reset token")}
              </span>
              <input
                autoComplete="one-time-code"
                className="w-full rounded-[20px] border border-white/10 bg-slate-950/50 px-4 py-3 text-base text-white outline-none transition placeholder:text-slate-500 focus:border-sky-300/70 focus:bg-slate-950/70"
                onChange={(event) => setToken(event.target.value)}
                placeholder={t("Paste token")}
                required
                value={token}
              />
            </label>
            <label className="block">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
                {t("New password")}
              </span>
              <input
                autoComplete="new-password"
                className="w-full rounded-[20px] border border-white/10 bg-slate-950/50 px-4 py-3 text-base text-white outline-none transition placeholder:text-slate-500 focus:border-sky-300/70 focus:bg-slate-950/70"
                onChange={(event) => setPassword(event.target.value)}
                placeholder={t("At least 8 characters")}
                required
                type="password"
                value={password}
              />
            </label>
            <label className="block">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
                {t("Confirm password")}
              </span>
              <input
                autoComplete="new-password"
                className="w-full rounded-[20px] border border-white/10 bg-slate-950/50 px-4 py-3 text-base text-white outline-none transition placeholder:text-slate-500 focus:border-sky-300/70 focus:bg-slate-950/70"
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder={t("Repeat password")}
                required
                type="password"
                value={confirmPassword}
              />
            </label>
          </div>

          {saved ? (
            <div className="mt-4 rounded-[20px] border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm leading-7 text-emerald-100">
              {t("Password updated. You can sign in with the new password now.")}
            </div>
          ) : null}

          {error ? (
            <div
              className="mt-4 flex items-start justify-between gap-3 rounded-[20px] border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100"
              role="alert"
            >
              <span>{error}</span>
              <button
                className="shrink-0 rounded-full border border-rose-200/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-rose-100 transition hover:bg-rose-200/10"
                onClick={() => setError(null)}
                type="button"
              >
                {t("Dismiss")}
              </button>
            </div>
          ) : null}

          <button
            className="mt-6 w-full rounded-full bg-sky-300 px-5 py-3 text-sm font-semibold uppercase tracking-[0.24em] text-slate-950 transition hover:bg-sky-200 disabled:cursor-not-allowed disabled:opacity-70"
            disabled={submitting}
            type="submit"
          >
            {submitting ? t("Updating...") : t("Update password")}
          </button>

          <p className="mt-6 text-xs leading-6 text-slate-400">
            <Link className="ops-action-link font-semibold text-sky-200" to="/sign-in">
              {t("Back to sign-in")}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
